// Copy for the two buyer-facing product pages (ProductDetail.jsx for a
// self-registered supplier's product, FactoryProductDetail.jsx for a catalog
// factory's) and components/product/ProductPageParts.jsx. Mirrors the mobile
// repo's src/lib/productPageCopy.js word for word — keep them identical.
//
// The trust strip's payment line describes the 30%/70% plan (deposit, balance
// before shipping) — the default of the two plans (see
// src/lib/supplierRequestTerms.js). Owner's wording, 2026-10-03.

const COPY = {
  ar: {
    back: 'رجوع',
    backToFactory: 'رجوع للمصنع',
    priceFrom: (p) => `من ${p} / قطعة`,
    priceOnRequest: 'السعر عند الطلب',
    moq: (n) => `أقل طلب: ${n}`,
    pieces: 'قطعة',
    trustVerified: 'مورد موثّق من معبر',
    trustPayment: 'دفعك محمي: ٣٠٪ عربون، والباقي قبل الشحن',
    trustFollow: 'معبر يتابع طلبك من المصنع لين يوصلك',
    readMore: 'اقرأ المزيد',
    readLess: 'عرض أقل',
    description: 'الوصف',
    seeFactory: 'شوف المصنع',
    verified: 'موثّق',
    requestQuote: 'اطلب عرض سعر',
    askFactory: 'اسأل المصنع',
    samples: 'العينات',
    samplesAvailable: 'متاحة',
    origin: 'بلد المنشأ',
    leadTime: 'مدة التجهيز',
    days: (a, b) => (b && b !== a ? `${a}–${b} يوم` : `${a} يوم`),
    specs: 'المواصفات',
    customization: 'خيارات التخصيص',
    refCode: 'رمز المنتج',
    reviews: 'تقييم',
    notFound: 'المنتج غير موجود',
    country: (v) => (/^china$/i.test(String(v || '').trim()) ? 'الصين' : v),
  },
  en: {
    back: 'Back',
    backToFactory: 'Back to factory',
    priceFrom: (p) => `From ${p} / piece`,
    priceOnRequest: 'Price on request',
    moq: (n) => `Min. order: ${n}`,
    pieces: 'pieces',
    trustVerified: 'Supplier verified by Maabar',
    trustPayment: 'Your payment is protected: 30% deposit, the rest before shipping',
    trustFollow: 'Maabar follows your order from the factory to your door',
    readMore: 'Read more',
    readLess: 'Show less',
    description: 'Description',
    seeFactory: 'View factory',
    verified: 'Verified',
    requestQuote: 'Request a quote',
    askFactory: 'Ask the factory',
    samples: 'Samples',
    samplesAvailable: 'Available',
    origin: 'Origin',
    leadTime: 'Lead time',
    days: (a, b) => (b && b !== a ? `${a}–${b} days` : `${a} days`),
    specs: 'Specifications',
    customization: 'Customization options',
    refCode: 'Ref',
    reviews: 'reviews',
    notFound: 'Product not found',
    country: (v) => v,
  },
  zh: {
    back: '返回',
    backToFactory: '返回工厂',
    priceFrom: (p) => `${p} 起 / 件`,
    priceOnRequest: '价格面议',
    moq: (n) => `起订量：${n}`,
    pieces: '件',
    trustVerified: 'Maabar 认证供应商',
    trustPayment: '付款有保障：30% 定金，余款发货前付清',
    trustFollow: 'Maabar 全程跟进您的订单，从工厂直到送达',
    readMore: '展开',
    readLess: '收起',
    description: '描述',
    seeFactory: '查看工厂',
    verified: '已认证',
    requestQuote: '请求报价',
    askFactory: '咨询工厂',
    samples: '样品',
    samplesAvailable: '可提供',
    origin: '原产地',
    leadTime: '交期',
    days: (a, b) => (b && b !== a ? `${a}–${b} 天` : `${a} 天`),
    specs: '规格',
    customization: '定制选项',
    refCode: '货号',
    reviews: '条评价',
    notFound: '未找到产品',
    country: (v) => (/^china$/i.test(String(v || '').trim()) ? '中国' : v),
  },
};

export function productPageCopy(lang = 'ar') {
  return COPY[lang] || COPY.en;
}

// Back label with the arrow pointing the way back: right in Arabic (RTL), left
// otherwise.
export function backLabel(lang, text) {
  return lang === 'ar' ? `→ ${text}` : `← ${text}`;
}

// "أقل طلب: 500 قطعة". The unit is appended only to a bare number — a catalog
// MOQ is free text ("500 pcs", "1 carton") and already carries its own.
// Returns '' when there is no MOQ, so the caller can hide the line.
export function moqText(moq, lang) {
  const raw = String(moq ?? '').trim();
  if (!raw || /^(0|—|–|-|not_found|n\/?a)$/i.test(raw)) return '';
  const c = productPageCopy(lang);
  if (/^\d[\d,]*$/.test(raw)) return c.moq(`${raw} ${c.pieces}`);
  // Free text in Latin ("1-50pcs") inside an Arabic line is reordered by the
  // bidi algorithm into "50pcs-1"; a left-to-right isolate keeps it as typed.
  return c.moq(lang === 'ar' && !/[\u0600-\u06FF]/.test(raw) ? `\u2066${raw}\u2069` : raw);
}

// "من 12.50 ر.س / قطعة" for a single amount; a range or tiered catalog price
// ("40 – 52 ر.س", "(1-49 PCS) 7 ر.س / …") is already a full sentence and is shown
// as it is. '' → caller shows "price on request".
export function priceFromText(price, lang) {
  const p = String(price || '').trim();
  if (!p) return '';
  const single = /^[\d,]+(\.\d+)?\s*(ر\.س|SAR)$/.test(p);
  return single ? productPageCopy(lang).priceFrom(p) : p;
}
